import { type Course, UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import { ChevronRightIcon, PackageIcon } from "lucide-react";
import { useState } from "react";

import CourseExamPortalModal from "./CourseExamPortalModal";
import Skeleton from "~/components/Skeleton";
import { useFinalExamAttempts, useUserAction } from "~/hooks";
import useFreeCourse from "~/hooks/useFreeCourse";
import usePassedCourses from "~/hooks/usePassedCourses";
import useTrackCTA from "~/hooks/useTrackCTA";
import { formatExamUnlockDistance } from "~/utils/formatExamUnlockDistance";

interface CourseExamsCardProps {
	course: Course;
	isMobile?: boolean;
	hasCourseAccess?: boolean;
	isLoadingCourseAccess: boolean;
}

const CourseExamsCard = ({
	course,
	hasCourseAccess,
	isLoadingCourseAccess,
}: CourseExamsCardProps) => {
	const { setUserAction } = useUserAction();
	const trackCTA = useTrackCTA();
	const { openClaimIfOffered } = useFreeCourse(course._id);
	const { isSignedIn, isLoaded } = useAuth();
	const { isCoursePassed, isLoadingPassedCourses } = usePassedCourses();

	const { data: finalExamAttempts, isLoading: isLoadingFinalExamAttempts } =
		useFinalExamAttempts();

	const [isExamPortalModalOpen, setIsExamPortalModalOpen] = useState(false);

	const isLoading =
		isLoadingCourseAccess ||
		isLoadingFinalExamAttempts ||
		isLoadingPassedCourses ||
		!isLoaded;

	const unlockDistance = formatExamUnlockDistance(
		finalExamAttempts?.unlockDate,
		{
			addSuffix: false,
			includeSeconds: false,
		},
	);

	if (isLoading)
		return (
			<Skeleton className="w-full rounded-xl! bg-slate-200 py-8 dark:bg-slate-700" />
		);

	return (
		<>
			<button
				disabled={course.isBeta}
				id="exam-portal-button"
				onClick={async () => {
					if (!isSignedIn) {
						trackCTA(UserAction.SIGN_UP, "Exams and Coaching Card");
						setUserAction(UserAction.SIGN_UP);
					} else if (!hasCourseAccess) {
						if (openClaimIfOffered()) return;
						trackCTA(UserAction.BECOME_A_MEMBER, "Exams and Coaching Card");
						setUserAction(UserAction.BECOME_A_MEMBER);
					} else {
						setIsExamPortalModalOpen(!isExamPortalModalOpen);
					}
				}}
				className="flex h-16 w-full shrink-0 items-center gap-3 rounded-xl border bg-white px-4 py-2 font-medium shadow shadow-slate-100 hover:bg-slate-50 disabled:pointer-events-none disabled:opacity-50 dark:bg-slate-700/50 dark:shadow-slate-900 dark:hover:bg-slate-700"
			>
				<PackageIcon size={24} className="text-sky-700 dark:text-sky-200" />

				<div className="flex flex-col text-base">
					<p className="text-left">Coaching & Exams</p>

					{hasCourseAccess ? (
						<p className="text-left text-sm">
							{isCoursePassed(course._id) ? (
								<span className="text-sky-800 dark:text-sky-200">
									Review your exam attempts
								</span>
							) : (finalExamAttempts?.remainingAttempts ?? 0) > 0 ? (
								<span className="text-sky-800 dark:text-sky-200">
									{finalExamAttempts?.remainingAttempts} Attempt
									{finalExamAttempts?.remainingAttempts === 1 ? "" : "s"}{" "}
									Remaining
								</span>
							) : (
								<span className="text-orange-800 capitalize dark:text-orange-200">
									{unlockDistance
										? `Next attempt in ${unlockDistance}`
										: "No attempts remaining"}
								</span>
							)}
						</p>
					) : (
						<p className="text-left font-normal text-sm">
							Take exam or see your previous attempts
						</p>
					)}
				</div>

				<ChevronRightIcon size={20} className="ml-auto" />
			</button>

			{isSignedIn && (
				<CourseExamPortalModal
					isOpen={isExamPortalModalOpen}
					hasCourseAccess={hasCourseAccess!}
					course={course}
					onClose={() => setIsExamPortalModalOpen(false)}
				/>
			)}
		</>
	);
};

export default CourseExamsCard;
